import React, { useRef } from 'react';
import styled from 'styled-components';

// Web Design & Hosting — XP-era promotional page for Dead Simple Computing
// Styled like an early-2000s web design company page

function WebDesign() {
  const webDesignRef = useRef(null);
  const webAppsRef = useRef(null);
  const appDesignRef = useRef(null);
  const biRef = useRef(null);
  const hostingRef = useRef(null);
  const maintenanceRef = useRef(null);
  const seoRef = useRef(null);

  const scrollTo = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Container>
      <div className="wd__header">
        <div className="wd__header__logo">🌐</div>
        <div className="wd__header__text">
          <div className="wd__header__title">Dead Simple Web Solutions</div>
          <div className="wd__header__subtitle">Web Design • Application Development • Business Intelligence • Hosting</div>
        </div>
        <div className="wd__header__badge">★ Est. 2004 ★</div>
      </div>

      <div className="wd__marquee">
        <marquee scrollamount="3">
          🔥 FREE website audit for new customers! 🔥 Custom web applications built to YOUR spec 🔥 Business Intelligence & Data Analytics now available 🔥 Call 0118 359 2220 today! 🔥
        </marquee>
      </div>

      <div className="wd__body">
        <div className="wd__sidebar">
          <div className="wd__sidebar__section">
            <div className="wd__sidebar__title">Our Services</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(webDesignRef)}>✏️ Website Design</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(webAppsRef)}>💻 Web Applications</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(appDesignRef)}>📱 Application Design</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(biRef)}>📊 Business Intelligence</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(hostingRef)}>🖥️ Application Hosting</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(maintenanceRef)}>🔧 Website Maintenance</div>
            <div className="wd__sidebar__link" onClick={() => scrollTo(seoRef)}>📈 SEO & Marketing</div>
          </div>
          <div className="wd__sidebar__section">
            <div className="wd__sidebar__title">Contact Us</div>
            <div className="wd__sidebar__info">
              📞 0118 359 2220<br />
              📧 hello@deadsimplecomputing.co.uk<br />
              🌐 deadsimplecomputing.co.uk
            </div>
          </div>
          <div className="wd__sidebar__counter">
            <div className="wd__counter">
              <span className="wd__counter__digit">0</span>
              <span className="wd__counter__digit">1</span>
              <span className="wd__counter__digit">3</span>
              <span className="wd__counter__digit">3</span>
              <span className="wd__counter__digit">7</span>
            </div>
            <div className="wd__counter__label">Visitors since 2004</div>
          </div>
        </div>

        <div className="wd__content">
          <div className="wd__section" ref={webDesignRef}>
            <h2 className="wd__section__title">🎨 Sick Websites. Dead Simple.</h2>
            <p>Still rocking a website from 2003? Using Comic Sans? Got a hit counter that's stuck at 12?</p>
            <p>Let us build you something <strong>actually impressive</strong>. We design modern, responsive websites that look mint on every device — from your nan's ancient laptop to the latest iPhone.</p>
            <div className="wd__cta">
              <a
                className="wd__cta__button"
                href="https://deadsimplecomputing.co.uk/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get a FREE Quote →
              </a>
              <span className="wd__cta__note">No obligation. No hard sell. Just straight-up honest pricing.</span>
            </div>
          </div>

          <div className="wd__section" ref={webAppsRef}>
            <h2 className="wd__section__title">💻 Custom Web Applications</h2>
            <p>Need something more than a website? We build <strong>bespoke web applications</strong> tailored to your business:</p>
            <table className="wd__table">
              <thead>
                <tr>
                  <th>What We Build</th>
                  <th>What You Get</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Booking Systems</td><td>Customers book 24/7 without phoning you</td></tr>
                <tr><td>Client Portals</td><td>Secure login area for your clients</td></tr>
                <tr><td>Custom CRMs</td><td>Track leads, sales, and customers YOUR way</td></tr>
                <tr><td>E-Commerce</td><td>Sell stuff online without the headache</td></tr>
                <tr><td>Internal Tools</td><td>Automate the boring admin work</td></tr>
                <tr><td>API Integrations</td><td>Connect everything together seamlessly</td></tr>
              </tbody>
            </table>
          </div>

          <div className="wd__section" ref={appDesignRef}>
            <h2 className="wd__section__title">📱 Application Design</h2>
            <p>From concept to launch, we design <strong>beautiful, intuitive applications</strong> that your users will actually enjoy using.</p>
            <table className="wd__table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>What's Included</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>UX Research</td><td>Understand your users before writing a single line of code</td></tr>
                <tr><td>UI Design</td><td>Pixel-perfect mockups and interactive prototypes</td></tr>
                <tr><td>Mobile Apps</td><td>iOS & Android apps that feel native</td></tr>
                <tr><td>Design Systems</td><td>Consistent, scalable component libraries</td></tr>
                <tr><td>Accessibility</td><td>WCAG compliant — everyone should be able to use your app</td></tr>
              </tbody>
            </table>
          </div>

          <div className="wd__section wd__section--highlight" ref={biRef}>
            <h2 className="wd__section__title">📊 Business Intelligence & Data Analytics</h2>
            <p>Drowning in spreadsheets? We turn your <strong>raw data into actionable insights</strong> that actually help you make decisions.</p>
            <div className="wd__why__grid">
              <div className="wd__why__item">
                <strong>📈 Custom Dashboards</strong>
                <p>Real-time dashboards tailored to your KPIs. See what matters at a glance — no more digging through Excel.</p>
              </div>
              <div className="wd__why__item">
                <strong>🔗 Data Integration</strong>
                <p>Connect all your systems — CRM, accounting, marketing — into one unified view.</p>
              </div>
              <div className="wd__why__item">
                <strong>🤖 AI & Automation</strong>
                <p>Machine learning models that predict trends, automate reporting, and spot opportunities you'd miss.</p>
              </div>
              <div className="wd__why__item">
                <strong>📋 Reporting</strong>
                <p>Automated reports delivered to your inbox. Weekly, monthly, or whenever you need them.</p>
              </div>
            </div>
          </div>

          <div className="wd__section" ref={hostingRef}>
            <h2 className="wd__section__title">🏠 Hosting That Actually Works</h2>
            <p>Tired of your website going down every time more than 3 people visit? Our hosting is built for businesses that need <strong>reliability</strong>, not excuses.</p>
            <div className="wd__hosting__grid">
              <div className="wd__hosting__plan wd__hosting__plan--featured">
                <div className="wd__hosting__name">Custom App Hosting</div>
                <div className="wd__hosting__price">From £19.99/mo</div>
                <ul className="wd__hosting__features">
                  <li>Any framework, any language</li>
                  <li>Node.js, PHP, Python, .NET</li>
                  <li>Dedicated resources</li>
                  <li>Auto-scaling available</li>
                  <li>Database hosting included</li>
                  <li>24/7 monitoring</li>
                </ul>
              </div>
              <div className="wd__hosting__plan">
                <div className="wd__hosting__name">Full Managed Package</div>
                <div className="wd__hosting__price">From £49.99/mo</div>
                <ul className="wd__hosting__features">
                  <li>Everything above, plus:</li>
                  <li>Monthly updates & patches</li>
                  <li>Performance monitoring</li>
                  <li>Priority support</li>
                  <li>Quarterly reviews</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="wd__section" ref={maintenanceRef}>
            <h2 className="wd__section__title">🔧 Website Maintenance</h2>
            <p>Got a website that needs TLC? We handle <strong>updates, security patches, and keeping things running</strong> so you don't have to worry.</p>
          </div>

          <div className="wd__section" ref={seoRef}>
            <h2 className="wd__section__title">📈 SEO & Marketing</h2>
            <p>What's the point of a great website if nobody can find it? We'll get you <strong>ranking on Google</strong> and actually bringing in customers.</p>
          </div>

          <div className="wd__section wd__section--highlight">
            <h2 className="wd__section__title">🤝 Why Choose Dead Simple?</h2>
            <div className="wd__why__grid">
              <div className="wd__why__item">
                <strong>🇬🇧 Proper British Company</strong>
                <p>Based in Reading, Berkshire. Real people, real phone number, real office. None of that "our team is based in 7 different time zones" nonsense.</p>
              </div>
              <div className="wd__why__item">
                <strong>🗣️ Plain English</strong>
                <p>We explain things without the jargon. If you don't understand something, we haven't done our job properly.</p>
              </div>
              <div className="wd__why__item">
                <strong>💷 Honest Pricing</strong>
                <p>No surprises, no hidden fees. We quote what it costs and that's what you pay. Revolutionary, we know.</p>
              </div>
              <div className="wd__why__item">
                <strong>🔒 Security First</strong>
                <p>Every site we build follows security best practices. Because getting hacked is not a great look for your business.</p>
              </div>
            </div>
          </div>

          <div className="wd__footer__cta">
            <p>Ready to get started? Give us a ring on <strong>0118 359 2220</strong> or drop us an email at <strong>hello@deadsimplecomputing.co.uk</strong></p>
            <p className="wd__footer__small">Dead Simple Computing Ltd • Registered in England & Wales • We promise not to use Comic Sans on your website.</p>
          </div>
        </div>
      </div>

      {/* Classic 90s dancing baby */}
      <img
        src="/dancing_baby.gif"
        alt="Dancing Baby"
        className="wd__dancing-baby"
      />

      <div className="wd__status">
        <span>📍 C:\Program Files\Dead Simple\Web Solutions</span>
        <span>🔒 Secure Connection</span>
      </div>
    </Container>
  );
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: Tahoma, 'Segoe UI', sans-serif;
  font-size: 12px;
  overflow: hidden;
  position: relative;

  .wd__header {
    background: linear-gradient(to right, #003366, #0066cc, #003366);
    color: #fff;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .wd__header__logo { font-size: 32px; }
  .wd__header__title { font-size: 18px; font-weight: bold; letter-spacing: 0.5px; }
  .wd__header__subtitle { font-size: 11px; opacity: 0.85; margin-top: 2px; }
  .wd__header__badge {
    margin-left: auto;
    background: #FFD700;
    color: #003366;
    padding: 4px 10px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 10px;
    white-space: nowrap;
  }

  .wd__marquee {
    background: #FFFFCC;
    border-bottom: 1px solid #CC9900;
    border-top: 1px solid #CC9900;
    font-size: 11px;
    font-weight: bold;
    color: #CC0000;
    padding: 3px 0;
    flex-shrink: 0;
  }

  .wd__body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .wd__sidebar {
    width: 180px;
    background: linear-gradient(to bottom, #E8F0FE, #D0E0F0);
    border-right: 1px solid #7F9DB9;
    padding: 10px;
    overflow-y: auto;
    flex-shrink: 0;
  }
  .wd__sidebar__section { margin-bottom: 16px; }
  .wd__sidebar__title {
    font-weight: bold;
    color: #003399;
    border-bottom: 1px solid #7F9DB9;
    padding-bottom: 3px;
    margin-bottom: 6px;
    font-size: 11px;
  }
  .wd__sidebar__link {
    color: #003399;
    cursor: pointer;
    padding: 3px 0;
    font-size: 11px;
    &:hover { text-decoration: underline; color: #0055CC; }
  }
  .wd__sidebar__info {
    font-size: 10px;
    color: #333;
    line-height: 1.6;
  }
  .wd__counter {
    display: flex;
    justify-content: center;
    gap: 2px;
    margin-top: 8px;
  }
  .wd__counter__digit {
    background: #000;
    color: #0F0;
    padding: 2px 5px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    font-weight: bold;
  }
  .wd__counter__label {
    text-align: center;
    font-size: 9px;
    color: #666;
    margin-top: 3px;
  }

  .wd__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;
  }

  .wd__section {
    margin-bottom: 24px;
  }
  .wd__section--highlight {
    background: #F5F8FF;
    border: 1px solid #C0D0E0;
    border-radius: 4px;
    padding: 16px;
  }
  .wd__section__title {
    color: #003366;
    font-size: 15px;
    font-weight: bold;
    margin: 0 0 8px 0;
    border-bottom: 2px solid #0066CC;
    padding-bottom: 4px;
  }
  .wd__section p {
    margin: 6px 0;
    line-height: 1.5;
    color: #333;
  }

  .wd__cta {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .wd__cta__button {
    background: linear-gradient(to bottom, #0066CC, #004499);
    color: #fff;
    padding: 8px 20px;
    border-radius: 3px;
    cursor: pointer;
    font-weight: bold;
    font-size: 12px;
    border: 1px solid #003366;
    text-decoration: none;
    display: inline-block;
    &:hover { background: linear-gradient(to bottom, #0077DD, #0055AA); color: #fff; }
    &:active { background: #003366; }
  }
  .wd__cta__note {
    font-size: 10px;
    color: #666;
    font-style: italic;
  }

  .wd__table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 11px;
  }
  .wd__table th {
    background: linear-gradient(to bottom, #003366, #004488);
    color: #fff;
    padding: 6px 10px;
    text-align: left;
    font-weight: bold;
  }
  .wd__table td {
    padding: 5px 10px;
    border-bottom: 1px solid #ddd;
  }
  .wd__table tr:nth-child(even) td { background: #F5F8FF; }
  .wd__table tr:hover td { background: #E0EEFF; }

  .wd__hosting__grid {
    display: flex;
    gap: 12px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .wd__hosting__plan {
    flex: 1;
    min-width: 150px;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 12px;
    background: #FAFAFA;
    text-align: center;
  }
  .wd__hosting__plan--featured {
    border-color: #0066CC;
    background: linear-gradient(to bottom, #E8F0FF, #D0E0FF);
    box-shadow: 0 2px 8px rgba(0,102,204,0.2);
  }
  .wd__hosting__name {
    font-weight: bold;
    color: #003366;
    font-size: 13px;
    margin-bottom: 4px;
  }
  .wd__hosting__price {
    font-size: 18px;
    font-weight: bold;
    color: #006600;
    margin-bottom: 8px;
  }
  .wd__hosting__features {
    list-style: none;
    padding: 0;
    margin: 0;
    text-align: left;
    font-size: 10px;
    li {
      padding: 2px 0;
      &:before { content: '✅ '; }
    }
  }

  .wd__why__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .wd__why__item {
    padding: 8px;
    p { font-size: 11px; margin: 4px 0 0; }
  }

  .wd__footer__cta {
    text-align: center;
    padding: 16px;
    background: linear-gradient(to bottom, #003366, #004488);
    color: #fff;
    border-radius: 4px;
    margin-top: 8px;
    p { margin: 4px 0; }
  }
  .wd__footer__small {
    font-size: 9px;
    opacity: 0.7;
    margin-top: 8px;
  }

  .wd__dancing-baby {
    position: absolute;
    bottom: 30px;
    left: 4px;
    width: 48px;
    height: auto;
    z-index: 10;
    image-rendering: pixelated;
    pointer-events: none;
  }

  .wd__status {
    background: #ECE9D8;
    border-top: 1px solid #aaa;
    padding: 3px 8px;
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #555;
    flex-shrink: 0;
  }
`;

export default WebDesign;
